import { ReactNode } from 'react';

export type FilterGroupType = 'Details' | 'Company' | 'Activity' | 'Advanced';

export interface ScoreRange {
  min: number;
  max: number;
}

export type FilterValueType = 
  | string 
  | string[] 
  | boolean 
  | number 
  | ScoreRange
  | Date
  | DateTimeRange;

export interface FilterOption {
  id: FilterId;
  label: string;
  group: FilterGroupType;
  icon?: ReactNode;
  placeholder?: string;
  helperText?: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: RegExp;
    errorMessage?: string;
  };
}

// All possible filter IDs
export type FilterId = 
  | 'name'
  | 'first_name'
  | 'last_name'
  | 'phone_number'
  | 'email'
  | 'email_not_contains'
  | 'address'
  | 'tags'
  | 'tags_not_contains'
  | 'stage'
  | 'source'
  | 'without_phone_number'
  | 'without_email'
  | 'favorite'
  | 'company_name'
  | 'company_size'
  | 'company_industry'
  | 'company_website'
  | 'company_address'
  // Consolidated datetime range filters
  | 'created_at_range'
  | 'updated_at_range'
  | 'last_contacted_at_range'
  | 'last_email_from_contact_range'
  | 'last_email_to_contact_range'
  | 'last_chat_from_contact_range'
  | 'last_chat_to_contact_range'
  | 'last_sms_from_contact_range'
  | 'last_sms_to_contact_range'
  | 'birthday'
  | 'metadata_contains'
  | 'metadata_not_contains'
  | 'contacted'
  | 'score'
  | 'limit'
  | 'inactive'
  | 'next_task'
  | 'last_activity'
  | 'last_communication'
  | 'timeframe';

export interface FilterValue {
  option: FilterOption;
  value: FilterValueType;
}

export interface FilterState {
  [key: string]: FilterValueType;
}

export type FilterInputType = 'text' | 'array' | 'boolean' | 'number' | 'score' | 'date' | 'select';

// Helper type for filter configurations
export interface FilterConfig {
  id: FilterId;
  label: string;
  group: FilterGroupType;
  type: FilterInputType;
  options?: string[];
  defaultValue: FilterValueType;
  placeholder?: string;
  helperText?: string;
  validation?: {
    min?: number;
    max?: number;
    pattern?: RegExp;
    errorMessage?: string;
  };
  icon?: ReactNode;
}

// Type guard to check if a value is a ScoreRange
export function isScoreRange(value: FilterValueType): value is ScoreRange {
  return (
    typeof value === 'object' &&
    value !== null &&
    'min' in value &&
    'max' in value &&
    typeof value.min === 'number' &&
    typeof value.max === 'number'
  );
}

// Type guard to check if a value is a Date
export function isDate(value: FilterValueType): value is Date {
  return value instanceof Date;
}

// DateTimeRange interface for range filters
export interface DateTimeRange {
  start?: Date;
  end?: Date;
  mode: 'before' | 'after' | 'between';
}

// Field mapping helpers
export const getBaseFieldName = (rangeFilterId: string): string => {
  // Convert range filter IDs to their base field names
  const mapping: Record<string, string> = {
    'created_at_range': 'created_at',
    'updated_at_range': 'updated_at',
    'last_contacted_at_range': 'last_contacted_at',
    'last_email_from_contact_range': 'last_email_from_contact',
    'last_email_to_contact_range': 'last_email_to_contact',
    'last_chat_from_contact_range': 'last_chat_from_contact',
    'last_chat_to_contact_range': 'last_chat_to_contact',
    'last_sms_from_contact_range': 'last_sms_from_contact',
    'last_sms_to_contact_range': 'last_sms_to_contact',
  };
  return mapping[rangeFilterId] || rangeFilterId;
};

export const isRangeFilter = (filterId: FilterId): boolean => {
  return filterId.endsWith('_range');
};

// Type guard functions
export const isStringArray = (value: any): value is string[] => {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
};

export const isDateTimeRange = (value: any): value is DateTimeRange => {
  return value !== null && 
         typeof value === 'object' && 
         'mode' in value &&
         ['before', 'after', 'between'].includes(value.mode) &&
         (value.start instanceof Date || value.start === undefined) &&
         (value.end instanceof Date || value.end === undefined);
};

export interface Filter {
  id: FilterId;
  label: string;
  value: FilterValueType;
}

export interface ContactFilters {
  [key: string]: FilterValueType;
}

 
